import { AddCircle as CreateIcon } from "@mui/icons-material";
import { Grid } from "@mui/material";
import { dayGermanFormat } from "misc/formatHelpers";
import PricePositionsInputs from "molecules/PricePositionsInputs";
import { ContactInfoInputs, DefaultToolbar } from "organisms";
import { BookingRecordType, InvoiceKindEnum, InvoiceRecordType } from "pages/Booking/Booking.types";
import { EventDateRecordType, EventRecordType } from "pages/Event/Event.types";
import { AttendeeRecordType } from "pages/SharedRecords.types";
import { useState } from "react";
import {
  AutocompleteInput,
  BooleanInput,
  FormDataConsumer,
  ReferenceInput,
  required,
  TextInput,
  useTranslate,
} from "react-admin";
import { FieldValues, SubmitHandler } from "react-hook-form";

import { SimpleBoxForm } from "../../atoms/Forms/index";
import { InvoiceFormData } from ".";
import InvoiceKindInput from "./InvoiceKindInput";
import InvoiceTemplateInput from "./InvoiceTemplateInput";
import OverridePriceInput from "./OverridePriceInput";

type CreateInvoiceFormPropsType = {
  parentInvoice?: InvoiceRecordType;
  bookingsWithAttendees: BookingRecordType<AttendeeRecordType>[];
  event: EventRecordType;
  handleSubmit: SubmitHandler<FieldValues>;
};

export default function CreateInvoiceForm(props: CreateInvoiceFormPropsType) {
  const { handleSubmit, bookingsWithAttendees, event, parentInvoice = null } = props;

  const translate = useTranslate();
  const [templateAvailable, setTemplateAvailable] = useState(false);

  const firstBooking = bookingsWithAttendees[0] || {};
  let initialPrice = bookingsWithAttendees.length === 1 ? firstBooking.price : null;

  if (parentInvoice) {
    initialPrice = parentInvoice.price;
  }

  const getInitialServicePeriod = (dates: EventDateRecordType[]) => {
    if (dates.length === 0) return undefined;
    if (dates.length === 1) {
      return dayGermanFormat(dates[0].day);
    } else {
      const firstDay = dayGermanFormat(dates[0].day);
      const lastDay = dayGermanFormat(dates[dates.length - 1].day);
      return `${firstDay} - ${lastDay}`;
    }
  };

  let initialKind, initialName;

  if (parentInvoice) {
    initialKind = InvoiceKindEnum.Correction;
    initialName = translate("invoice.titles.correction", {
      invoiceNumber: parentInvoice.invoice_number,
    });
  } else {
    initialKind = InvoiceKindEnum.Standard;
    initialName = translate("invoice.titles.standard");
  }

  const defaultValues: Partial<InvoiceRecordType> = {
    kind: initialKind,
    price: initialPrice,
    name: initialName,
    service_period: getInitialServicePeriod(event.dates),
    parent: parentInvoice ? parentInvoice.id : null,
  };

  if (bookingsWithAttendees.length === 1) {
    defaultValues.invoice_contact_info = firstBooking.attendee.invoice_contact_info;
    defaultValues.title = firstBooking.attendee.title;
    defaultValues.first_name = firstBooking.attendee.first_name;
    defaultValues.last_name = firstBooking.attendee.last_name;
  }

  const toolbar = (
    <DefaultToolbar
      alwaysEnableSaveButton
      saveButtonLabel="ra.action.create"
      saveButtonIcon={<CreateIcon />}
      disabled={!templateAvailable}
    />
  );

  return (
    <SimpleBoxForm defaultValues={defaultValues} resource="invoices" onSubmit={handleSubmit} toolbar={toolbar}>
      <Grid container spacing={3}>
        {parentInvoice && (
          <Grid item xs={12}>
            <InvoiceKindInput parentInvoice={parentInvoice} initialPrice={initialPrice} helperText={false} />
          </Grid>
        )}

        <InvoiceTemplateInput
          setTemplateAvailable={setTemplateAvailable}
          validate={required()}
          initialKind={initialKind}
        />

        <Grid item xs={12}>
          <TextInput source="name" multiline validate={required()} helperText={false} fullWidth />
        </Grid>

        <Grid item xs={12}>
          <TextInput source="service_period" validate={required()} helperText={false} fullWidth />
        </Grid>

        <Grid item xs={12}>
          <OverridePriceInput selectedBookings={bookingsWithAttendees} invoiceKind={initialKind} />

          <FormDataConsumer<InvoiceFormData>>
            {({ formData, ...rest }) =>
              formData.override_prices && (
                <ReferenceInput {...rest} source="price.price_type" reference="price_types" perPage={50}>
                  <AutocompleteInput label="resources.prices.fields.price_type" optionText="name" />
                </ReferenceInput>
              )
            }
          </FormDataConsumer>

          <FormDataConsumer<InvoiceFormData>>
            {({ formData, ...rest }) =>
              formData.override_prices && <PricePositionsInputs {...rest} sourcePrefix="price" fullWidth />
            }
          </FormDataConsumer>
          <BooleanInput source="override_contact_info" sx={{ mt: 1 }} />
          <FormDataConsumer<InvoiceFormData>>
            {({ formData, ...rest }) =>
              formData.override_contact_info && (
                <Grid container columnSpacing={2}>
                  <Grid item xs={12} md={2}>
                    <TextInput label="resources.users.fields.title" source="title" />
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <TextInput label="resources.users.fields.first_name" source="first_name" validate={required()} />
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <TextInput label="resources.users.fields.last_name" source="last_name" validate={required()} />
                  </Grid>
                  <Grid item xs={12}>
                    <ContactInfoInputs
                      contactInfoType={"invoice_contact_info"}
                      omitFields={["email", "phone", "mobile", "fax"]}
                      inCreate
                      {...rest}
                    />
                  </Grid>
                </Grid>
              )
            }
          </FormDataConsumer>
        </Grid>
      </Grid>
    </SimpleBoxForm>
  );
}
